import React, { Fragment, Suspense, useEffect, useMemo, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import { createRoot } from 'react-dom/client';
import ReactGA from 'react-ga';
import { BrowserRouter, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import FindPassword from '@auth/password';
// Authentication
import Signin from '@auth/signin';
import SignUp from '@auth/signup';
import App from '@components/app';
import Modals from '@components/atom/Modals';
import ErrorBoundary from '@components/common/ErrorBoundary';
import { CenteredLoading } from '@components/Loading';
import MobileAppInstallPopup from '@components/modal/MobileAppInstallPopup';
import ConfigDB from '@data/customizer/config';
import DeeplinkForwarder from '@pages/deeplink/DeeplinkForwarder';
import ModalsProvider from '@store/ModalsProvider';
import useAppStore from '@store/useAppStore';
import GlobalStyle from '@theme/global';
import theme from '@theme/index';
import { getAllToken } from '@util/token';
import { ThemeProvider } from 'styled-components';

import PageBarcode from '@/pages/external/barcode';
import WebReceipt from '@/pages/external/webReceipt';

import {
  backofficeRoute,
  merchantUserRoute,
  publicRoute,
  routes,
  termsRoute,
  userRoute,
  v1Route,
  v2Route,
} from './route';

import 'react-toastify/dist/ReactToastify.css';
import './index.scss';

interface UserTypeState {
  admin: boolean;
  adminMerchant: boolean;
  merchantUserV1: boolean;
  merchantUserV2: boolean;
  userV2: boolean;
  merchant: boolean;
}

interface RedirectWithActionProps {
  UserType: UserTypeState;
}

const ScrollToTop = (): null => {
  const history = useHistory();

  useEffect(() => {
    const unlisten = history.listen(() => {
      window.scrollTo(0, 0);
    });

    return () => {
      unlisten();
    };
  }, [history]);

  return null;
};

const Root = (): React.ReactElement => {
  const [anim, setAnim] = useState('');
  const animation = localStorage.getItem('animation') || ConfigDB.data.router_animation || 'fade';
  const abortController = new AbortController();

  const { merchantKioskVersion, userPhoneNumber, loading, setLoading } = useAppStore();
  const { userToken, merchantToken, adminToken } = getAllToken();

  const jwt_token = userToken || merchantToken || adminToken;

  const UserType = useMemo<UserTypeState>(() => {
    return {
      admin: !!adminToken && !merchantToken,
      adminMerchant: !!merchantToken && !!adminToken,
      merchantUserV1: !!merchantToken && !userToken && !!userPhoneNumber,
      merchantUserV2: !!merchantToken && !!userToken,
      userV2: !!userToken && !merchantToken,
      merchant: !!merchantToken && !userToken,
    };
  }, [userToken, merchantToken, adminToken, userPhoneNumber]);

  const TRACKING_ID = process.env.React_APP_GOOGLE_ANALYTICS_TRACKING_ID;
  if (TRACKING_ID) ReactGA.initialize(TRACKING_ID);

  useEffect(() => {
    setAnim(animation);
    localStorage.setItem('isClick', 'false');
    (console as any).ignoredYellowBox = ['Warning: Each', 'Warning: Failed'];
    (console as any).disableYellowBox = true;
    return function cleanup() {
      abortController.abort();
    };
    // eslint-disable-next-line
  }, []);

  const isLoading = loading;
  const setIsLoading = setLoading;

  const Routes = useMemo(() => {
    const { userV2, merchantUserV1, merchantUserV2, admin } = UserType;
    if (userV2) return userRoute;
    if (merchantUserV1 || merchantUserV2) return merchantUserRoute;
    if (admin) return backofficeRoute;
    return routes;
  }, [UserType]);

  const MerchantVersionRoutes = useMemo(() => {
    try {
      if (merchantKioskVersion > 1) return v2Route;
      return v1Route;
    } catch (error) {
      return v1Route;
    }
  }, [merchantKioskVersion]);

  return (
    <Fragment>
      <ModalsProvider>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          <BrowserRouter basename={`/`}>
            <ScrollToTop />
            <Modals />
            <MobileAppInstallPopup />
            <Switch>
              {publicRoute.map(({ path, Component }) => (
                <Route key={path} exact path={`${process.env.PUBLIC_URL}${path}`}>
                  <Suspense fallback={<CenteredLoading />}>
                    <Component />
                  </Suspense>
                </Route>
              ))}
              <Route>
              {!jwt_token ? (
              <Switch>
                <Route path={`${process.env.PUBLIC_URL}/auth/login`} component={Signin} />
                <Route path={`${process.env.PUBLIC_URL}/auth/join`} component={SignUp} />
                <Route path={`${process.env.PUBLIC_URL}/auth/password`} component={FindPassword} />
                <Route path={`${process.env.PUBLIC_URL}/receipt/:token`} component={WebReceipt} />
                <Route
                  path={`${process.env.PUBLIC_URL}/barcode/:usercode`}
                  component={PageBarcode}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/deeplink/:link`}
                  component={DeeplinkForwarder}
                />
                {termsRoute.map(({ path, Component }) => (
                  <Route key={path} exact path={`${process.env.PUBLIC_URL}${path}`}>
                    <Suspense fallback={<CenteredLoading />}>
                      <Component />
                    </Suspense>
                  </Route>
                ))}
                <Route
                  path="*"
                  exact
                  render={() => (
                    <Route path="*" render={() => <RedirectWithAction UserType={UserType} />} />
                  )}
                />
              </Switch>
            ) : (
              <App isLoading={isLoading}>
                <TransitionGroup>
                  <Switch>
                    {[...Routes, ...MerchantVersionRoutes].map(({ path, Component }) => (
                      <Route key={path} exact path={`${process.env.PUBLIC_URL}${path}`}>
                        {({ match }) => (
                          <CSSTransition
                            in={match != null}
                            timeout={100}
                            classNames={anim}
                            unmountOnExit>
                            <div>
                              <Suspense fallback={<CenteredLoading />}>
                                <Component setIsLoading={setIsLoading} />
                              </Suspense>
                            </div>
                          </CSSTransition>
                        )}
                      </Route>
                    ))}
                    <Route path="*" render={() => <RedirectWithAction UserType={UserType} />} />
                    <Route
                      path={`${process.env.PUBLIC_URL}/barcode/:usercode`}
                      component={PageBarcode}
                    />
                    <Route
                      path={`${process.env.PUBLIC_URL}/deeplink/:link`}
                      component={DeeplinkForwarder}
                    />
                    <Route
                      path="*"
                      render={() => (
                        <Redirect
                          to={
                            UserType.merchant || UserType.adminMerchant
                              ? '/admin/dashboard'
                              : UserType.userV2
                              ? '/select-merchant'
                              : UserType.admin
                              ? '/backoffice/merchant/list'
                              : UserType.merchantUserV1 || UserType.merchantUserV2
                              ? '/dashboard'
                              : '/auth/login'
                          }
                        />
                      )}
                    />
                  </Switch>
                </TransitionGroup>
              </App>
            )}
              </Route>
            </Switch>
          </BrowserRouter>
        </ThemeProvider>
      </ModalsProvider>
    </Fragment>
  );
};

const RedirectWithAction = ({ UserType }: RedirectWithActionProps): React.ReactElement | null => {
  const { pathname } = useLocation();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    const redirectTo =
      UserType.merchant || UserType.adminMerchant
        ? '/admin/dashboard'
        : UserType.userV2
        ? '/select-merchant'
        : UserType.admin
        ? '/backoffice/merchant/list'
        : UserType.merchantUserV1 || UserType.merchantUserV2
        ? '/dashboard'
        : '/auth/login';

    if (redirectTo === '/auth/login') localStorage.setItem('locationBeforeRedirection', pathname);
    setShouldRedirect(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return shouldRedirect ? (
    <Redirect
      to={
        UserType.merchant || UserType.adminMerchant
          ? '/admin/dashboard'
          : UserType.userV2
          ? '/select-merchant'
          : UserType.admin
          ? '/backoffice/merchant/list'
          : UserType.merchantUserV1 || UserType.merchantUserV2
          ? '/dashboard'
          : '/auth/login'
      }
    />
  ) : null;
};

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);
root.render(
  <ErrorBoundary>
    <CookiesProvider>
      <Root />
    </CookiesProvider>
  </ErrorBoundary>,
);
